import resourcesService from '../services/resourcesService';

const MAX_RESOURCE_TYPE_COUNT = 100;
const MAX_RESOURCE_ITEM_COUNT = 10;

const RESOURCE_TYPES_PAGE_SIZE = MAX_RESOURCE_TYPE_COUNT;
const RESOURCES_PAGE_SIZE = MAX_RESOURCE_TYPE_COUNT * MAX_RESOURCE_ITEM_COUNT;

const state = {
  resourceTypes: [],
  resources: [],
  isLoadingResourceTypes: false,
  isLoadingResources: false,
  hasLoadedResourceTypes: false,
  hasLoadedResources: false,
};

const getters = {
  resourceTypes: (state) => state.resourceTypes,
  resources: (state) => state.resources,
  isLoadingResourceTypes: (state) => state.isLoadingResourceTypes,
  isLoadingResources: (state) => state.isLoadingResources,
  hasLoadedResourceTypes: (state) => state.hasLoadedResourceTypes,
  hasLoadedResources: (state) => state.hasLoadedResources,
  getResourceTypeByUid: (state) => (uid) => state.resourceTypes.find(type => type.uid === uid),
  getResourceByUid: (state) => (uid) => state.resources.find(resource => resource.uid === uid),
  getResourceTypeByResourceUid: (state) => (resourceUid) => {
    const resource = state.resources.find(resource => resource.uid === resourceUid);
    return resource ? state.resourceTypes.find(type => type.uid === resource.resource_type_uid) : null;
  },
  getResourcesByTypeUid: (state) => (typeUid) => state.resources.filter(resource => resource.resource_type_uid === typeUid),
};

const mutations = {
  setResourceTypes(state, resourceTypes) {
    state.resourceTypes = resourceTypes;
  },
  setResources(state, resources) {
    state.resources = resources;
  },
  setLoadingResourceTypes(state, isLoading) {
    state.isLoadingResourceTypes = isLoading;
  },
  setLoadingResources(state, isLoading) {
    state.isLoadingResources = isLoading;
  },
  setHasLoadedResourceTypes(state, hasLoaded) {
    state.hasLoadedResourceTypes = hasLoaded;
  },
  setHasLoadedResources(state, hasLoaded) {
    state.hasLoadedResources = hasLoaded;
  },
  resetResourceTypes(state) {
    state.resourceTypes = [];
    state.hasLoadedResourceTypes = false;
  },
  resetResources(state) {
    state.resources = [];
    state.hasLoadedResources = false;
  },
};

const actions = {
  async fetchResourceTypes({ commit, state }, { pageIndex = 1, pageSize = RESOURCE_TYPES_PAGE_SIZE, sort = 'created_at:desc' } = {}) {
    if (state.isLoadingResourceTypes) return;
    
    commit('setLoadingResourceTypes', true);
    
    try {
      const resourceTypes = await resourcesService.fetchResourceTypes(pageIndex, pageSize, sort);
      commit('setResourceTypes', resourceTypes || []);
      commit('setHasLoadedResourceTypes', true);
      return resourceTypes;
    } catch (error) {
      console.error('Error fetching resource types:', error);
      commit('setResourceTypes', []);
    } finally {
      commit('setLoadingResourceTypes', false);
    }
  },

  async fetchResources({ commit, state }, { pageIndex = 1, pageSize = RESOURCES_PAGE_SIZE, filter = {} } = {}) {
    if (state.isLoadingResources) return;
    
    commit('setLoadingResources', true);
    
    try {
      const resources = await resourcesService.fetchResources(pageIndex, pageSize, filter);
      commit('setResources', resources || []);
      commit('setHasLoadedResources', true);
      return resources;
    } catch (error) {
      console.error('Error fetching resources:', error);
      commit('setResources', []);
    } finally {
      commit('setLoadingResources', false);
    }
  },
  async loadResourceTypes({ commit, dispatch, state }, options = {}) {
    if (!state.hasLoadedResourceTypes || options.forceReload) {
      return await dispatch('fetchResourceTypes', options);
    }
    return state.resourceTypes;
  },
  async loadResources({ commit, dispatch, state }, options = {}) {
    if (!state.hasLoadedResources || options.forceReload) {
      return await dispatch('fetchResources', options);
    }
    return state.resources;
  },
  async loadResourcesByServiceUid({ commit, state }, serviceUid) {
    const resources = await resourcesService.fetchResources(1, RESOURCES_PAGE_SIZE, { service_uid: serviceUid });
    commit('setResources', resources || []);
  },
  resetResourceTypesData({ commit }) {
    commit('resetResourceTypes');
  },

  resetResourcesData({ commit }) {
    commit('resetResources');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}; 